import { useCallback, useState } from "react";

import { getLatestDialogue } from "@/features/socratic/helpers/get-latest-dialogue";
import {
  SocraticDialogueFragment,
  SocraticSessionFragment,
  useCreateSocraticDialogueMutation,
} from "@/generated/graphql";

export type CreateMetadata = {
  attemptNumber: {
    prev: number | null;
    next: number;
  };
};

export function useCreateDialogue() {
  const [mutation, { loading, data, error }] =
    useCreateSocraticDialogueMutation();

  const [createMetadata, setCreateMetadata] = useState<CreateMetadata | null>(
    null
  );

  const createDialogue = useCallback(
    ({
      workId,
      session,
      onSuccess,
    }: {
      workId: string;
      session: SocraticSessionFragment;
      onSuccess?: ({
        updatedSession,
        newDialogue,
      }: {
        updatedSession: SocraticSessionFragment;
        newDialogue: SocraticDialogueFragment;
      }) => void;
    }) => {
      const { id: sessionId, dialogues } = session;
      const latestDialogue = getLatestDialogue(dialogues);

      const newAttemptNumber = latestDialogue
        ? latestDialogue.attemptNumber + 1
        : 1;

      setCreateMetadata({
        attemptNumber: {
          prev: latestDialogue?.attemptNumber ?? null,
          next: newAttemptNumber,
        },
      });

      return mutation({
        variables: {
          workId,
          sessionId,
          attemptNumber: newAttemptNumber,
        },
        onCompleted: (mutationData) => {
          const updatedSession = mutationData.createSocraticDialogue.session;
          const newDialogue = updatedSession
            ? getLatestDialogue(updatedSession.dialogues)
            : null;
          if (updatedSession && newDialogue) {
            onSuccess?.({ updatedSession, newDialogue });
          }
        },
      });
    },
    [mutation]
  );

  return {
    createDialogue,
    creatingDialogue: loading,
    createError: !!error || data?.createSocraticDialogue.error != null,
    createMetadata,
  };
}
